import type { ReactElement } from 'react';
import { Box } from '@mantine/core';
import { adoptionBand } from './AdoptionBands';

/** Band boundaries (Emerging / Established / Leading) drawn as ticks on the track. */
const THRESHOLDS = [25, 50, 75];

interface AdoptionScoreBarProps {
    /** Score 0–100. */
    value: number;
    height?: number;
}

/**
 * A 0–100 track that places a score against the adoption maturity bands: the fill
 * runs to the score in that band's color, with ticks at the 25/50/75 boundaries so
 * the bare number reads as "where in the journey" at a glance. Colors come from
 * {@link adoptionBand} (design tokens), never hardcoded.
 */
export default function AdoptionScoreBar({ value, height = 8 }: AdoptionScoreBarProps): ReactElement {
    const clamped = Math.max(0, Math.min(100, value));
    const band = adoptionBand(value);
    return (
        <Box style={{ position: 'relative', height, background: 'var(--bm-color-surface-alt)', borderRadius: 999 }}>
            <Box style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${clamped}%`, minWidth: clamped > 0 ? 4 : 0, borderRadius: 999, background: band.color }} />
            {THRESHOLDS.map((tick) => (
                <Box key={tick} style={{ position: 'absolute', top: -2, bottom: -2, left: `${tick}%`, width: 2, borderRadius: 2, background: 'var(--bm-color-body)', opacity: 0.6 }} />
            ))}
        </Box>
    );
}
